// Which language the quiz shows, and applying it everywhere it matters.
//
// Order: ?lang=es|en  ->  saved choice (language switch or an earlier ?lang)
// ->  Spanish browser (navigator.languages has es-*), organic traffic, AND the
// visitor is in the web47_spanish_quiz variation  ->  English.
//
// web47 is organic only (Steven, Oct 28 2026): paid-ad visitors (the same rule
// web46 uses) get English and no web47 exposure, because the paid-ad Bridge
// CMS copy is English. ?lang=es and a force pin still work on any traffic.
//
// Only displayed text changes. Answer values, plan ids and every analytics,
// Core, Stripe and HubSpot payload stay English; the language itself is sent
// as quiz_language (Amplitude user property + every event's properties).
import * as amplitude from "@amplitude/analytics-browser";
import { Identify } from "@amplitude/analytics-browser";
import type { PrecomputedEvaluationsInterface, StatsigClient } from "@statsig/js-client";
import type { Model } from "survey-core";
import i18n, { QuizLanguage } from "./index";
import { getAssignedExperimentArm, getForcedExperimentArm, isPaidAdTraffic } from "../utils";

export const WEB47_EXPERIMENT_NAME = "web47_spanish_quiz";
export const LANGUAGE_STORAGE_KEY = "bws_quiz_language";
export const LANGUAGE_QUERY_PARAM = "lang";

function asLanguage(value: string | null | undefined): QuizLanguage | null {
  const v = (value || "").trim().toLowerCase();
  return v === "es" || v === "en" ? v : null;
}

function readSavedLanguage(): QuizLanguage | null {
  try {
    return asLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function saveLanguageChoice(lang: QuizLanguage): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // Storage blocked: the choice lasts for this page load only.
  }
}

/** ?lang= on this URL (saved so it survives the quiz's own navigation), else the saved choice. */
export function getExplicitLanguage(search: string = window.location.search): QuizLanguage | null {
  const fromUrl = asLanguage(new URLSearchParams(search).get(LANGUAGE_QUERY_PARAM));
  if (fromUrl) {
    saveLanguageChoice(fromUrl);
    return fromUrl;
  }
  return readSavedLanguage();
}

export function browserPrefersSpanish(nav: Pick<Navigator, "languages" | "language"> = navigator): boolean {
  const langs = nav.languages && nav.languages.length ? nav.languages : [nav.language];
  return langs.some((l) => /^es(-|$)/i.test(l || ""));
}

/**
 * Resolve the language once Statsig is ready. web47 is only evaluated (and its
 * exposure only logged) for organic Spanish-browser visitors without an explicit choice.
 */
export function resolveQuizLanguage(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): QuizLanguage {
  const explicit = getExplicitLanguage();
  if (explicit) return explicit;
  if (!browserPrefersSpanish() || !client) return "en";
  if (isPaidAdTraffic() && !getForcedExperimentArm(WEB47_EXPERIMENT_NAME)) return "en";
  const arm = getAssignedExperimentArm(WEB47_EXPERIMENT_NAME, client);
  if (arm === "control" || arm === "variation") trackWeb47Exposure(arm);
  return arm === "variation" ? "es" : "en";
}

function trackWeb47Exposure(arm: string): void {
  try {
    if (localStorage.getItem("bws_web47_exposed")) return;
    localStorage.setItem("bws_web47_exposed", "1");
  } catch {
    // Storage blocked: one exposure per page load.
  }
  amplitude.track("ExperimentStarted", { experimentName: WEB47_EXPERIMENT_NAME, variantName: arm });
}

/** The switch shows for visitors who see Spanish or who picked a language themselves. */
export function shouldShowLanguageSwitch(lang: QuizLanguage): boolean {
  return lang === "es" || readSavedLanguage() !== null;
}

let amplitudePluginAdded = false;
let identifiedLanguage: QuizLanguage | null = null;

function tagAmplitude(lang: QuizLanguage): void {
  if (!amplitudePluginAdded) {
    amplitudePluginAdded = true;
    amplitude.add({
      name: "quiz-language",
      type: "enrichment",
      execute: async (event) => {
        event.event_properties = {
          ...(event.event_properties || {}),
          quiz_language: i18n.language === "es" ? "es" : "en",
        };
        return event;
      },
    });
  }
  if (identifiedLanguage !== lang) {
    identifiedLanguage = lang;
    const identify = new Identify();
    identify.set("quiz_language", lang);
    amplitude.identify(identify);
  }
}

/** Point i18next, <html lang>, SurveyJS and Amplitude at `lang`. */
export function applyQuizLanguage(lang: QuizLanguage, survey?: Model): void {
  if (i18n.language !== lang) i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
  tagAmplitude(lang);
  if (survey) applySurveyLanguage(survey, lang);
}

export function applySurveyLanguage(survey: Model, lang: QuizLanguage): void {
  const surveyLocale = lang === "en" ? "" : lang;
  if (survey.locale === surveyLocale) return;
  survey.locale = surveyLocale;
  // Calculated values (e.g. goalsText) are text built by functions that read
  // the active language; rerun them so a mid-quiz switch updates the copy.
  survey.calculatedValues.forEach((cv) => (cv as unknown as { rerunExpression: () => void }).rerunExpression());
  survey.locStrsChanged();
}
